import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Shield, Globe, Wrench, Target, Bug, AlertTriangle, Activity } from 'lucide-react';

const AddThreatActorModal = ({ isOpen, onClose, onSubmit, editData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    summary: '',
    first_seen: '',
    last_activity: '',
    sophistication_level: 'Medium',
    motivation: 'Financial',
    status: 'Active',
    sectors: [],
    countries: [],
    tools: [],
    mitre_techniques: [],
    iocs: [],
    cves: [],
    ioas: []
  });

  const [currentSector, setCurrentSector] = useState('');
  const [currentCountry, setCurrentCountry] = useState('');
  const [currentTool, setCurrentTool] = useState({ tool_name: '', tool_description: '' });
  const [currentMitre, setCurrentMitre] = useState({ technique_id: '', technique_name: '', tactic: '' });
  const [currentIoc, setCurrentIoc] = useState({ ioc_type: 'IP', ioc_value: '', description: '' });
  const [currentCve, setCurrentCve] = useState({ cve_id: '', description: '', severity: 'Medium' });
  const [currentIoa, setCurrentIoa] = useState({ indicator_type: '', indicator_description: '' });

  // Common sectors for dropdown
  const commonSectors = [
    'Financial Services', 'Healthcare', 'Government', 'Technology', 'Energy',
    'Defense', 'Telecommunications', 'Retail', 'Manufacturing', 'Education',
    'Media', 'Transportation', 'Hospitality', 'Aerospace', 'Pharmaceuticals'
  ];

  // MITRE ATT&CK Tactics
  const mitreTactics = [
    'Reconnaissance', 'Resource Development', 'Initial Access', 'Execution',
    'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Credential Access',
    'Discovery', 'Lateral Movement', 'Collection', 'Command and Control',
    'Exfiltration', 'Impact'
  ];

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        summary: editData.summary || '',
        first_seen: editData.first_seen || '',
        last_activity: editData.last_activity || '',
        sophistication_level: editData.sophistication_level || 'Medium',
        motivation: editData.motivation || 'Financial',
        status: editData.status || 'Active',
        sectors: editData.sectors?.map(s => s.sector) || [],
        countries: editData.countries?.map(c => c.country) || [],
        tools: editData.tools || [],
        mitre_techniques: editData.mitre_techniques || [],
        iocs: editData.iocs || [],
        cves: editData.cves || [],
        ioas: editData.ioas || []
      });
    } else {
      // Reset form for new entry
      setFormData({
        name: '',
        summary: '',
        first_seen: '',
        last_activity: '',
        sophistication_level: 'Medium',
        motivation: 'Financial',
        status: 'Active',
        sectors: [],
        countries: [],
        tools: [],
        mitre_techniques: [],
        iocs: [],
        cves: [],
        ioas: []
      });
    }
  }, [editData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Sector functions
  const addSector = () => {
    if (currentSector.trim() && !formData.sectors.includes(currentSector.trim())) {
      setFormData({ ...formData, sectors: [...formData.sectors, currentSector.trim()] });
      setCurrentSector('');
    }
  };

  const removeSector = (index) => {
    setFormData({ ...formData, sectors: formData.sectors.filter((_, i) => i !== index) });
  };

  // Country functions
  const addCountry = () => {
    if (currentCountry.trim() && !formData.countries.includes(currentCountry.trim())) {
      setFormData({ ...formData, countries: [...formData.countries, currentCountry.trim()] });
      setCurrentCountry('');
    }
  };

  const removeCountry = (index) => {
    setFormData({ ...formData, countries: formData.countries.filter((_, i) => i !== index) });
  };

  // Tool functions
  const addTool = () => {
    if (currentTool.tool_name.trim()) {
      setFormData({ ...formData, tools: [...formData.tools, { ...currentTool }] });
      setCurrentTool({ tool_name: '', tool_description: '' });
    }
  };

  const removeTool = (index) => {
    setFormData({ ...formData, tools: formData.tools.filter((_, i) => i !== index) });
  };

  // MITRE functions
  const addMitre = () => {
    if (currentMitre.technique_id.trim()) {
      setFormData({ ...formData, mitre_techniques: [...formData.mitre_techniques, { ...currentMitre }] });
      setCurrentMitre({ technique_id: '', technique_name: '', tactic: '' });
    }
  };

  const removeMitre = (index) => {
    setFormData({ ...formData, mitre_techniques: formData.mitre_techniques.filter((_, i) => i !== index) });
  };

  // IOC functions
  const addIoc = () => {
    if (currentIoc.ioc_value.trim()) {
      setFormData({ ...formData, iocs: [...formData.iocs, { ...currentIoc }] });
      setCurrentIoc({ ioc_type: 'IP', ioc_value: '', description: '' });
    }
  };

  const removeIoc = (index) => {
    setFormData({ ...formData, iocs: formData.iocs.filter((_, i) => i !== index) });
  };

  // CVE functions
  const addCve = () => {
    if (currentCve.cve_id.trim()) {
      setFormData({ ...formData, cves: [...formData.cves, { ...currentCve }] });
      setCurrentCve({ cve_id: '', description: '', severity: 'Medium' });
    }
  };

  const removeCve = (index) => {
    setFormData({ ...formData, cves: formData.cves.filter((_, i) => i !== index) });
  };

  // IOA functions
  const addIoa = () => {
    if (currentIoa.indicator_description.trim()) {
      setFormData({ ...formData, ioas: [...formData.ioas, { ...currentIoa }] });
      setCurrentIoa({ indicator_type: '', indicator_description: '' });
    }
  };

  const removeIoa = (index) => {
    setFormData({ ...formData, ioas: formData.ioas.filter((_, i) => i !== index) });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-cyan-500/30 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-cyan-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-100">
              {editData ? 'Edit Threat Actor' : 'Add Threat Actor'}
            </h2>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Basic Information Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" /> Basic Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Threat Actor Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., APT28, Lazarus Group"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Status</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Dormant">Dormant</option>
                    <option value="Defunct">Defunct</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">First Seen</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                    value={formData.first_seen}
                    onChange={(e) => setFormData({ ...formData, first_seen: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Last Activity</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                    value={formData.last_activity}
                    onChange={(e) => setFormData({ ...formData, last_activity: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Sophistication Level</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                    value={formData.sophistication_level}
                    onChange={(e) => setFormData({ ...formData, sophistication_level: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">Motivation</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                    value={formData.motivation}
                    onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                  >
                    <option value="Financial">Financial</option>
                    <option value="Espionage">Espionage</option>
                    <option value="Hacktivism">Hacktivism</option>
                    <option value="Destructive">Destructive</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-slate-400 mb-2">Summary</label>
                <textarea
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  rows={4}
                  placeholder="Brief description of the threat actor, their tactics, and known campaigns..."
                />
              </div>
            </div>

            {/* Target Sectors Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-400" /> Target Sectors
              </h3>
              
              <div className="flex gap-2 mb-3">
                <select
                  className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                  value={currentSector}
                  onChange={(e) => setCurrentSector(e.target.value)}
                >
                  <option value="">Select or type sector...</option>
                  {commonSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentSector}
                  onChange={(e) => setCurrentSector(e.target.value)}
                  placeholder="Or type custom sector"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSector())}
                />
                <button 
                  type="button" 
                  onClick={addSector} 
                  className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.sectors.map((sector, index) => (
                  <span key={index} className="px-3 py-1.5 bg-purple-500/20 text-purple-300 rounded-lg text-sm border border-purple-500/30 flex items-center gap-2">
                    {sector}
                    <button type="button" onClick={() => removeSector(index)} className="hover:text-purple-100">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Target Countries Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" /> Target Countries
              </h3>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentCountry}
                  onChange={(e) => setCurrentCountry(e.target.value)}
                  placeholder="Enter country name"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCountry())}
                />
                <button 
                  type="button" 
                  onClick={addCountry} 
                  className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.countries.map((country, index) => (
                  <span key={index} className="px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-lg text-sm border border-blue-500/30 flex items-center gap-2">
                    {country}
                    <button type="button" onClick={() => removeCountry(index)} className="hover:text-blue-100">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Tools Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-cyan-400" /> Tools & Malware
              </h3>
              
              <div className="grid grid-cols-12 gap-2 mb-3">
                <input
                  type="text"
                  className="col-span-4 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentTool.tool_name}
                  onChange={(e) => setCurrentTool({ ...currentTool, tool_name: e.target.value })}
                  placeholder="Tool name"
                />
                <input
                  type="text"
                  className="col-span-7 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentTool.tool_description}
                  onChange={(e) => setCurrentTool({ ...currentTool, tool_description: e.target.value })}
                  placeholder="Description (optional)"
                />
                <button 
                  type="button" 
                  onClick={addTool} 
                  className="col-span-1 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {formData.tools.map((tool, index) => (
                  <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex justify-between items-center">
                    <div>
                      <strong className="text-slate-200 text-sm">{tool.tool_name}</strong>
                      {tool.tool_description && <div className="text-xs text-slate-400 mt-1">{tool.tool_description}</div>}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeTool(index)} 
                      className="p-1.5 hover:bg-red-500/20 rounded transition-all text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* MITRE ATT&CK Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" /> MITRE ATT&CK Techniques
              </h3>
              
              <div className="grid grid-cols-12 gap-2 mb-3">
                <input
                  type="text"
                  className="col-span-2 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentMitre.technique_id}
                  onChange={(e) => setCurrentMitre({ ...currentMitre, technique_id: e.target.value })}
                  placeholder="T1234"
                />
                <input
                  type="text"
                  className="col-span-5 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentMitre.technique_name}
                  onChange={(e) => setCurrentMitre({ ...currentMitre, technique_name: e.target.value })}
                  placeholder="Technique name"
                />
                <select
                  className="col-span-4 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                  value={currentMitre.tactic}
                  onChange={(e) => setCurrentMitre({ ...currentMitre, tactic: e.target.value })}
                >
                  <option value="">Select tactic</option>
                  {mitreTactics.map(tactic => (
                    <option key={tactic} value={tactic}>{tactic}</option>
                  ))}
                </select>
                <button 
                  type="button" 
                  onClick={addMitre} 
                  className="col-span-1 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {formData.mitre_techniques.map((technique, index) => (
                  <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex justify-between items-center">
                    <div>
                      <span className="font-mono text-cyan-400 text-sm">{technique.technique_id}</span>
                      <span className="text-slate-200 text-sm ml-3">{technique.technique_name}</span>
                      {technique.tactic && <span className="ml-2 text-xs text-slate-400">({technique.tactic})</span>}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeMitre(index)} 
                      className="p-1.5 hover:bg-red-500/20 rounded transition-all text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* IOCs Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-cyan-400" /> Indicators of Compromise (IOCs)
              </h3>
              
              <div className="grid grid-cols-12 gap-2 mb-3">
                <select
                  className="col-span-2 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                  value={currentIoc.ioc_type}
                  onChange={(e) => setCurrentIoc({ ...currentIoc, ioc_type: e.target.value })}
                >
                  <option value="IP">IP</option>
                  <option value="Domain">Domain</option>
                  <option value="Hash">Hash</option>
                  <option value="Email">Email</option>
                  <option value="URL">URL</option>
                </select>
                <input
                  type="text"
                  className="col-span-4 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentIoc.ioc_value}
                  onChange={(e) => setCurrentIoc({ ...currentIoc, ioc_value: e.target.value })}
                  placeholder="IOC value"
                />
                <input
                  type="text"
                  className="col-span-5 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentIoc.description}
                  onChange={(e) => setCurrentIoc({ ...currentIoc, description: e.target.value })}
                  placeholder="Description (optional)"
                />
                <button 
                  type="button" 
                  onClick={addIoc} 
                  className="col-span-1 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {formData.iocs.map((ioc, index) => (
                  <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex justify-between items-start">
                    <div>
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs mr-2">{ioc.ioc_type}</span>
                      <strong className="font-mono text-slate-200 text-sm">{ioc.ioc_value}</strong>
                      {ioc.description && <div className="text-xs text-slate-400 mt-1">{ioc.description}</div>}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeIoc(index)} 
                      className="p-1.5 hover:bg-red-500/20 rounded transition-all text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* CVEs Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Bug className="w-5 h-5 text-cyan-400" /> Associated CVEs
              </h3>
              
              <div className="grid grid-cols-12 gap-2 mb-3">
                <input
                  type="text"
                  className="col-span-3 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentCve.cve_id}
                  onChange={(e) => setCurrentCve({ ...currentCve, cve_id: e.target.value })}
                  placeholder="CVE-2024-0000"
                />
                <input
                  type="text"
                  className="col-span-6 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentCve.description}
                  onChange={(e) => setCurrentCve({ ...currentCve, description: e.target.value })}
                  placeholder="Description"
                />
                <select
                  className="col-span-2 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                  value={currentCve.severity}
                  onChange={(e) => setCurrentCve({ ...currentCve, severity: e.target.value })}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
                <button 
                  type="button" 
                  onClick={addCve} 
                  className="col-span-1 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {formData.cves.map((cve, index) => (
                  <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex justify-between items-start">
                    <div>
                      <span className="font-mono text-slate-200 text-sm">{cve.cve_id}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                        cve.severity === 'Critical' ? 'bg-red-500/20 text-red-400' :
                        cve.severity === 'High' ? 'bg-orange-500/20 text-orange-400' :
                        cve.severity === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {cve.severity}
                      </span>
                      {cve.description && <div className="text-xs text-slate-400 mt-1">{cve.description}</div>}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeCve(index)} 
                      className="p-1.5 hover:bg-red-500/20 rounded transition-all text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* IOAs Section */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-400" /> Indicators of Attack (IOAs)
              </h3>
              
              <div className="grid grid-cols-12 gap-2 mb-3">
                <input
                  type="text"
                  className="col-span-3 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentIoa.indicator_type}
                  onChange={(e) => setCurrentIoa({ ...currentIoa, indicator_type: e.target.value })}
                  placeholder="Pattern type"
                />
                <input
                  type="text"
                  className="col-span-8 px-3 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                  value={currentIoa.indicator_description}
                  onChange={(e) => setCurrentIoa({ ...currentIoa, indicator_description: e.target.value })}
                  placeholder="Behavioral pattern description"
                />
                <button 
                  type="button" 
                  onClick={addIoa} 
                  className="col-span-1 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 transition-all"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                {formData.ioas.map((ioa, index) => (
                  <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 flex justify-between items-center">
                    <div className="text-sm text-slate-200">
                      {ioa.indicator_type && <strong>{ioa.indicator_type}: </strong>}
                      {ioa.indicator_description}
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeIoa(index)} 
                      className="p-1.5 hover:bg-red-500/20 rounded transition-all text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/30 flex justify-end gap-3">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700/70 rounded-lg text-sm text-slate-300 transition-all"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-sm text-white font-medium transition-all"
            >
              {editData ? 'Update Threat Actor' : 'Add Threat Actor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddThreatActorModal;